import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { protocolsApi, tasksApi, usersApi, User, ProjectMember } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { SpeechToText } from '../components/SpeechToText';
import { LoadingSpinner } from '../components/LoadingScreen';
import { useTelegram } from '../hooks/useTelegram';
import { X, UserPlus, Search, Check, ChevronDown, Plus, Users } from 'lucide-react';

const priorityOptions = [
  { value: 'CURRENT', label: 'Текущая', color: 'bg-green-500' },
  { value: 'HIGH', label: 'Важная', color: 'bg-orange-500' },
  { value: 'CRITICAL', label: 'Сверхважная', color: 'bg-red-500' },
];

const statusOptions = [
  { value: 'CREATED', label: 'Задача поставлена' },
  { value: 'IN_PROGRESS', label: 'В процессе' },
];

export function CreateTaskPage() {
  const { protocolId } = useParams<{ protocolId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hapticFeedback, showAlert, webApp } = useTelegram();

  const defaultSectionId = searchParams.get('sectionId');

  const [form, setForm] = useState({
    text: '',
    sectionId: defaultSectionId || '',
    priority: 'CURRENT',
    status: 'CREATED',
    startDate: format(new Date(), 'yyyy-MM-dd'),
    dueDate: '',
  });

  const [selectedAssignees, setSelectedAssignees] = useState<User[]>([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [showSectionPicker, setShowSectionPicker] = useState(false);
  const [showPriorityPicker, setShowPriorityPicker] = useState(false);
  const [showStatusPicker, setShowStatusPicker] = useState(false);
  const [showAddNew, setShowAddNew] = useState(false);
  const [newAssignee, setNewAssignee] = useState({ firstName: '', lastName: '' });
  const [mainButtonActive, setMainButtonActive] = useState(false);

  const { data: protocol, isLoading, error } = useQuery({
    queryKey: ['protocol', protocolId],
    queryFn: () => protocolsApi.get(protocolId!),
    enabled: !!protocolId,
  });

  // Получаем участников проекта
  const { data: projectMembers } = useQuery({
    queryKey: ['projectMembers', protocol?.projectId],
    queryFn: () => usersApi.getProjectMembers(protocol!.projectId),
    enabled: !!protocol?.projectId,
  });

  const { data: searchResults } = useQuery({
    queryKey: ['users', 'search', userSearchQuery],
    queryFn: () => usersApi.search(userSearchQuery),
    enabled: userSearchQuery.length >= 2,
  });

  const createMutation = useMutation({
    mutationFn: tasksApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['protocol', protocolId] });
      hapticFeedback('success');
      showAlert('Задача создана!');
      navigate(-1);
    },
    onError: (err: Error) => {
      showAlert(`Ошибка: ${err.message}`);
      hapticFeedback('error');
    },
  });

  // Мутация для создания нового ответственного
  const createAssigneeMutation = useMutation({
    mutationFn: usersApi.createAssignee,
    onSuccess: (user) => {
      addAssignee(user);
      setShowAddNew(false);
      setNewAssignee({ firstName: '', lastName: '' });
      queryClient.invalidateQueries({ queryKey: ['projectMembers'] });
      hapticFeedback('success');
    },
    onError: (err: Error) => {
      showAlert(`Ошибка: ${err.message}`);
      hapticFeedback('error');
    },
  });

  // Настраиваем Main Button (для Telegram)
  useEffect(() => {
    if (!webApp?.MainButton) {
      setMainButtonActive(false);
      return;
    }

    const canSubmit = form.text.trim() && form.sectionId;

    if (canSubmit) {
      webApp.MainButton.setText('✓ Создать задачу');
      webApp.MainButton.show();
      webApp.MainButton.enable();
      webApp.MainButton.color = '#2563eb';
      setMainButtonActive(true);
    } else {
      webApp.MainButton.hide();
      setMainButtonActive(false);
    }

    const handleSubmit = () => {
      if (!canSubmit || createMutation.isPending) return;
      handleCreate();
    };

    webApp.MainButton.onClick(handleSubmit);

    return () => {
      webApp.MainButton.offClick(handleSubmit);
      webApp.MainButton.hide();
      setMainButtonActive(false);
    };
  }, [webApp, form, selectedAssignees, protocolId, createMutation.isPending]);

  const handleCreate = () => {
    if (!form.text.trim() || !form.sectionId) return;
    
    createMutation.mutate({
      protocolId: protocolId!,
      sectionId: form.sectionId,
      text: form.text.trim(),
      priority: form.priority as 'CRITICAL' | 'HIGH' | 'CURRENT',
      status: form.status,
      startDate: form.startDate || null,
      dueDate: form.dueDate || null,
      assigneeIds: selectedAssignees.map((u) => u.id),
    });
  };

  const handleSpeechResult = (text: string) => {
    setForm((prev) => ({
      ...prev,
      text: prev.text + (prev.text ? ' ' : '') + text,
    }));
    hapticFeedback('success');
  };

  const addAssignee = (user: User | ProjectMember) => {
    // Преобразуем ProjectMember в User для совместимости
    const userForAssignee: User = {
      id: user.id,
      telegramId: user.telegramId || '', // User требует string, не null
      firstName: user.firstName,
      lastName: user.lastName || null,
      username: user.username || null,
      photoUrl: user.photoUrl || null,
    };
    if (!selectedAssignees.find((u) => u.id === user.id)) {
      setSelectedAssignees([...selectedAssignees, userForAssignee]);
      hapticFeedback('light');
    }
    setShowUserSearch(false);
    setUserSearchQuery('');
  };

  const removeAssignee = (userId: string) => {
    setSelectedAssignees(selectedAssignees.filter((u) => u.id !== userId));
    hapticFeedback('light');
  };

  const handleCreateNewAssignee = () => {
    if (!newAssignee.firstName.trim()) return;
    
    createAssigneeMutation.mutate({
      firstName: newAssignee.firstName.trim(),
      lastName: newAssignee.lastName.trim() || undefined,
      projectId: protocol?.projectId,
    });
  };

  const selectedSection = protocol?.sections?.find(s => s.id === form.sectionId);
  const selectedPriority = priorityOptions.find(p => p.value === form.priority);
  const selectedStatus = statusOptions.find(s => s.value === form.status);

  const canSubmit = form.text.trim() && form.sectionId;

  // Фильтруем участников проекта - исключаем уже выбранных
  const availableMembers = projectMembers?.filter(
    m => !selectedAssignees.some(s => s.id === m.id)
  ) || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !protocol) {
    return (
      <div className="p-4 text-center">
        <p className="text-tg-hint mb-4">
          {error ? `Ошибка: ${(error as Error).message}` : 'Протокол не найден'}
        </p>
        <Button onClick={() => navigate(-1)}>Назад</Button>
      </div>
    );
  }

  const sections = protocol?.sections || [];

  return (
    <div className="min-h-screen bg-gray-100 pb-32">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-3">
        <h1 className="text-lg font-semibold">Новая задача</h1>
        <p className="text-sm text-white/80 truncate">{protocol.title}</p>
      </div>

      <div className="p-4 space-y-4">
        {/* Текст задачи */}
        <Card className="p-4">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-600">Текст задачи *</label>
            <SpeechToText onResult={handleSpeechResult} />
          </div>
          <textarea
            value={form.text}
            onChange={(e) => setForm({ ...form, text: e.target.value })}
            placeholder="Введите или продиктуйте текст задачи..."
            rows={4}
            className="w-full px-4 py-3 rounded-xl bg-gray-50 text-gray-900 placeholder:text-gray-400 border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none resize-none text-base"
          />
        </Card>

        {/* Раздел */}
        <Card className="p-4">
          <label className="text-sm font-medium text-gray-600 block mb-2">Раздел *</label>
          <button
            onClick={() => setShowSectionPicker(!showSectionPicker)}
            className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-left"
          >
            <span className={selectedSection ? 'text-gray-900' : 'text-gray-400'}>
              {selectedSection 
                ? `${selectedSection.number}. ${selectedSection.title}`
                : 'Выберите раздел'
              }
            </span>
            <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showSectionPicker ? 'rotate-180' : ''}`} />
          </button>
          
          {showSectionPicker && (
            <div className="mt-2 bg-white rounded-xl overflow-hidden border border-gray-200 shadow-lg animate-slide-down">
              {sections.length === 0 ? (
                <div className="p-4 text-center text-gray-400">
                  Нет доступных разделов
                </div>
              ) : (
                sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => {
                      setForm({ ...form, sectionId: section.id });
                      setShowSectionPicker(false);
                      hapticFeedback('light');
                    }}
                    className={`w-full px-4 py-3 text-left flex items-center justify-between transition-colors ${
                      form.sectionId === section.id 
                        ? 'bg-blue-50 text-blue-600' 
                        : 'hover:bg-gray-50 text-gray-900'
                    }`}
                  >
                    <span>{section.number}. {section.title}</span>
                    {form.sectionId === section.id && (
                      <Check className="w-5 h-5 text-blue-500" />
                    )}
                  </button>
                ))
              )}
            </div>
          )}
        </Card>

        {/* Ответственные */}
        <Card className="p-4">
          <label className="text-sm font-medium text-gray-600 block mb-2">Ответственные</label>
          
          {/* Выбранные пользователи */}
          {selectedAssignees.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {selectedAssignees.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-100 text-blue-700 rounded-full"
                >
                  <span className="text-sm font-medium">
                    {user.lastName || ''} {user.firstName}
                  </span>
                  <button
                    onClick={() => removeAssignee(user.id)}
                    className="text-blue-500 hover:text-red-500 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Интерфейс добавления */}
          {showUserSearch ? (
            <div className="space-y-3 animate-slide-down">
              {/* Участники проекта */}
              {availableMembers.length > 0 && !showAddNew && (
                <div>
                  <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    Участники проекта
                  </p>
                  <div className="bg-gray-50 rounded-xl border border-gray-200 max-h-40 overflow-auto">
                    {availableMembers.map((member) => (
                      <button
                        key={member.id}
                        onClick={() => addAssignee(member)}
                        className="w-full px-4 py-3 text-left hover:bg-white transition-colors flex items-center justify-between border-b border-gray-100 last:border-b-0"
                      >
                        <div>
                          <span className="text-gray-900 font-medium">
                            {member.lastName || ''} {member.firstName}
                          </span>
                          {member.username && (
                            <span className="text-gray-400 ml-1 text-sm">@{member.username}</span>
                          )}
                        </div>
                        <Plus className="w-4 h-4 text-blue-500" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Поиск */}
              {!showAddNew && (
                <div>
                  <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                    <Search className="w-3 h-3" />
                    Поиск по имени
                  </p>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Введите имя..."
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                  
                  {searchResults && searchResults.length > 0 && (
                    <div className="mt-2 bg-white rounded-xl border border-gray-200 shadow-lg max-h-40 overflow-auto">
                      {searchResults.map((user) => (
                        <button
                          key={user.id}
                          onClick={() => addAssignee(user)}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center justify-between border-b border-gray-100 last:border-b-0"
                          disabled={selectedAssignees.some((u) => u.id === user.id)}
                        >
                          <div>
                            <span className="text-gray-900">
                              {user.lastName || ''} {user.firstName}
                            </span>
                            {user.username && (
                              <span className="text-gray-400 ml-1 text-sm">@{user.username}</span>
                            )}
                          </div>
                          {selectedAssignees.some((u) => u.id === user.id) ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Plus className="w-4 h-4 text-blue-500" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {userSearchQuery.length >= 2 && (!searchResults || searchResults.length === 0) && (
                    <p className="text-sm text-gray-400 text-center py-3">
                      Не найдено
                    </p>
                  )}
                </div>
              )}

              {/* Добавить нового */}
              {showAddNew ? (
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <p className="text-sm font-medium text-blue-700 mb-3">Новый ответственный</p>
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder="Имя *"
                      value={newAssignee.firstName}
                      onChange={(e) => setNewAssignee({ ...newAssignee, firstName: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg bg-white border border-blue-200 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none"
                      autoFocus
                    />
                    <input
                      type="text"
                      placeholder="Фамилия"
                      value={newAssignee.lastName}
                      onChange={(e) => setNewAssignee({ ...newAssignee, lastName: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg bg-white border border-blue-200 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none"
                    />
                    <div className="flex gap-2 pt-2">
                      <Button
                        onClick={handleCreateNewAssignee}
                        disabled={!newAssignee.firstName.trim()}
                        isLoading={createAssigneeMutation.isPending}
                        size="sm"
                        className="flex-1"
                      >
                        Добавить
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setShowAddNew(false);
                          setNewAssignee({ firstName: '', lastName: '' });
                        }}
                      >
                        Отмена
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowAddNew(true)}
                  className="w-full px-4 py-3 rounded-xl border-2 border-dashed border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-500 transition-colors flex items-center justify-center gap-2"
                >
                  <UserPlus className="w-4 h-4" />
                  Добавить нового человека
                </button>
              )}

              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowUserSearch(false);
                  setUserSearchQuery('');
                  setShowAddNew(false);
                }}
                className="w-full"
              >
                Закрыть
              </Button>
            </div>
          ) : (
            <Button
              variant="secondary"
              onClick={() => setShowUserSearch(true)}
              className="w-full gap-2"
            >
              <UserPlus className="w-4 h-4" />
              Добавить ответственного
            </Button>
          )}
        </Card>

        {/* Даты */}
        <Card className="p-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-600 block mb-2">Дата начала</label>
              <input
                type="date"
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-gray-50 text-gray-900 border border-gray-200 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600 block mb-2">Срок</label>
              <input
                type="date"
                value={form.dueDate}
                onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-gray-50 text-gray-900 border border-gray-200 focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>
        </Card>

        {/* Приоритет */}
        <Card className="p-4">
          <label className="text-sm font-medium text-gray-600 block mb-2">Важность</label>
          <button
            onClick={() => setShowPriorityPicker(!showPriorityPicker)}
            className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-left"
          >
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${selectedPriority?.color}`}></div>
              <span className="text-gray-900">{selectedPriority?.label}</span>
            </div>
            <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showPriorityPicker ? 'rotate-180' : ''}`} />
          </button>
          
          {showPriorityPicker && (
            <div className="mt-2 bg-white rounded-xl overflow-hidden border border-gray-200 shadow-lg animate-slide-down">
              {priorityOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    setForm({ ...form, priority: option.value });
                    setShowPriorityPicker(false);
                    hapticFeedback('light');
                  }}
                  className={`w-full px-4 py-3 text-left flex items-center justify-between transition-colors ${
                    form.priority === option.value 
                      ? 'bg-blue-50' 
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${option.color}`}></div>
                    <span className="text-gray-900">{option.label}</span>
                  </div>
                  {form.priority === option.value && (
                    <Check className="w-5 h-5 text-blue-500" />
                  )}
                </button>
              ))}
            </div>
          )}
        </Card>

        {/* Статус */}
        <Card className="p-4">
          <label className="text-sm font-medium text-gray-600 block mb-2">Статус</label>
          <button
            onClick={() => setShowStatusPicker(!showStatusPicker)}
            className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-left"
          >
            <span className="text-gray-900">{selectedStatus?.label}</span>
            <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showStatusPicker ? 'rotate-180' : ''}`} />
          </button>
          
          {showStatusPicker && (
            <div className="mt-2 bg-white rounded-xl overflow-hidden border border-gray-200 shadow-lg animate-slide-down">
              {statusOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    setForm({ ...form, status: option.value });
                    setShowStatusPicker(false);
                    hapticFeedback('light');
                  }}
                  className={`w-full px-4 py-3 text-left flex items-center justify-between transition-colors ${
                    form.status === option.value 
                      ? 'bg-blue-50' 
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <span className="text-gray-900">{option.label}</span>
                  {form.status === option.value && (
                    <Check className="w-5 h-5 text-blue-500" />
                  )}
                </button>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Фиксированная кнопка внизу - только если MainButton не активна */}
      {!mainButtonActive && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 safe-area-bottom">
          <Button
            onClick={handleCreate}
            isLoading={createMutation.isPending}
            disabled={!canSubmit}
            className="w-full btn-primary"
            size="lg"
          >
            {canSubmit ? 'Создать задачу' : 'Заполните обязательные поля'}
          </Button>
        </div>
      )}
    </div>
  );
}
